import { createWorkshopRecordStore } from "@/lib/create-workshop-record-store";
import {
  OPPORTUNITY_STATUSES,
  OPPORTUNITY_STATUS_DEFAULT,
  type OpportunityStatus,
} from "@/lib/taxonomy";

/**
 * Opportunities register (Track A · REG-OPP). An opportunity is a candidate
 * for action the workshop surfaces — "we could…" — distinct from a finding
 * (an observation) and a recommendation (a proposed course). It carries its
 * own lifecycle (`identified → exploring → pursued`, or `discarded`), which
 * is why it's a sibling store rather than a tagged finding: the status set
 * genuinely differs. Built on {@link createWorkshopRecordStore}.
 *
 * Free-form tags on an opportunity persist **namespaced** as
 * `opportunity:<tag>` (see `TAG_NS_OPPORTUNITY`) at the form layer, so the
 * Insight Library can facet opportunity themes apart from other tags.
 */

export type { OpportunityStatus };

export type Opportunity = {
  id: string;
  workshopId: string;
  title: string;
  /** Free-text body — what the opportunity is. */
  detail: string;
  status: OpportunityStatus;
  /** Who owns exploring it (display name). */
  owner: string;
  createdAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 seam), persisted `opportunity:<tag>`-namespaced. */
  tags?: string[];
};

export type OpportunityPatch = Partial<
  Pick<Opportunity, "title" | "detail" | "status" | "owner" | "tags">
>;

export const useOpportunities = createWorkshopRecordStore<
  Opportunity,
  OpportunityPatch
>({
  idbKey: "xaes-opportunities",
  recordType: "opportunities",
  sanitiseItem: (o, fallbackWorkshopId) => {
    if (typeof o.id !== "string") return null;
    if (typeof o.title !== "string") return null;
    const status: OpportunityStatus = OPPORTUNITY_STATUSES.includes(
      o.status as OpportunityStatus,
    )
      ? (o.status as OpportunityStatus)
      : OPPORTUNITY_STATUS_DEFAULT;
    return {
      id: o.id,
      workshopId:
        typeof o.workshopId === "string" ? o.workshopId : fallbackWorkshopId,
      title: o.title,
      detail: typeof o.detail === "string" ? o.detail : "",
      status,
      owner: typeof o.owner === "string" ? o.owner : "",
      createdAt: typeof o.createdAt === "number" ? o.createdAt : Date.now(),
      addedBy: typeof o.addedBy === "string" ? o.addedBy : "",
      tags: Array.isArray(o.tags)
        ? o.tags.filter((t): t is string => typeof t === "string")
        : undefined,
    };
  },
});
